import pick from 'lodash/pick';
import { ITrackerData, IWidgetSettings } from '../../../types';
import { APPOINTMENT_CHANNELS, PATIENT_STATUS } from '../../constants';
import { decodePrefillData } from '../../utils/session/prefillToken';
import toUSDateString from '../../utils/toUSDateString';
import { ScheduleRequestAttemptData } from '../SchedulingSessionState/types';
import { DEPENDENT_STATUS } from './constants';
import {
  AppointmentFormValues,
  AppointmentFormValuesCurrentUser,
  AppointmentFormValuesOpening,
  AppointmentFormValuesProvider,
  DependentStatus,
  PatientStatus,
} from './types';

const MIN_AGE = 18;

type Query = Record<string, string | null>;

export interface GetInitialValuesOptions {
  currentUser?: AppointmentFormValuesCurrentUser | null;
  provider?: AppointmentFormValuesProvider | null;
  opening?: AppointmentFormValuesOpening | null;
  query?: Query | null;
  widgetSettings?: IWidgetSettings | null;
  prefillToken?: string | null;
  trackerData?: ITrackerData | null;
  scheduleRequestAttemptData?: ScheduleRequestAttemptData | null;
  isOnWidget?: boolean | null;
  isScheduleRequest?: boolean | null;
}

export default function getInitialValues({
  currentUser = null,
  provider = null,
  opening = null,
  query,
  widgetSettings,
  prefillToken,
  trackerData,
  scheduleRequestAttemptData = null,
  isOnWidget = false,
  isScheduleRequest = false,
}: GetInitialValuesOptions = {}): AppointmentFormValues {
  const initialValues: AppointmentFormValues = {
    // Form state,
    patientStatus: PATIENT_STATUS.NEW as PatientStatus,
    dependentStatus: DEPENDENT_STATUS.ME as DependentStatus,
    createDependent: false,
    createAccount: false,
    // Actual data fields,
    provider,
    opening,
    reasonForVisit: null,
    insuranceIssuer: null,
    user: currentUser,
    patient: null,
    patientGivenName: '',
    patientFamilyName: '',
    patientBirthDate: '',
    contactGivenName: '',
    contactFamilyName: '',
    contactBirthDate: '',
    contactEmail: '',
    contactMobilePhone: '',
    username: '',
    password: '',
    termsAgreement: false,
    memberId: '',
    group: '',
    employer: '',
    subscriberGivenName: '',
    subscriberFamilyName: '',
    subscriberBirthDate: '',
    subscriberPostalCode: '',
    subscriberSsn: '',
    partnerId: null,
    widgetId: null,
    source: null,
    scheduleRequestAttemptData,
    ...decodePrefillData(prefillToken),
  };

  if (scheduleRequestAttemptData) {
    const { appointmentVerificationToken, scheduleRequest } = scheduleRequestAttemptData;
    const { patient, contactPatient } = scheduleRequest;
    initialValues.patientGivenName = patient.givenName;
    initialValues.patientFamilyName = patient.familyName;
    initialValues.patientBirthDate = toUSDateString(patient.birthdate) || '';
    initialValues.contactGivenName = contactPatient.givenName;
    initialValues.contactFamilyName = contactPatient.familyName;
    initialValues.contactBirthDate = toUSDateString(contactPatient.birthdate) || '';
    initialValues.contactEmail = scheduleRequest.contactEmail || contactPatient.email || '';
    initialValues.contactMobilePhone =
      scheduleRequest.contactMobilePhone || contactPatient.mobilePhoneNumber || '';
    initialValues.verificationToken = appointmentVerificationToken;
  }

  const { profile: { agesTreated: { max = null } = {} } = {} } = provider || {};
  if (max && max < MIN_AGE) {
    initialValues.dependentStatus = DEPENDENT_STATUS.DEPENDENT as DependentStatus;
  }

  if (currentUser && !currentUser.patient) {
    initialValues.patientGivenName = currentUser.givenName;
    initialValues.patientFamilyName = currentUser.familyName;
    initialValues.contactEmail = currentUser.username;
  }

  if (opening) {
    const reasonForVisitId = query && query.reason_for_visit_id;
    if (reasonForVisitId) {
      const reasonForVisit = opening.reasonsForVisit.nodes.find(
        (reason) => reason.id === reasonForVisitId
      );
      if (reasonForVisit) {
        initialValues.reasonForVisit = reasonForVisit;
        initialValues.patientStatus = reasonForVisit.patientStatus;
      }
    }

    if (!initialValues.reasonForVisit) {
      const patientStatus = query && query.patient_status_id;
      if (patientStatus && patientStatus.toUpperCase() === PATIENT_STATUS.RETURNING) {
        initialValues.patientStatus = PATIENT_STATUS.RETURNING as PatientStatus;
      }
      initialValues.reasonForVisit = opening.reasonsForVisit.nodes.find(
        (reason) => reason.patientStatus === initialValues.patientStatus
      );
    }
  }

  if (widgetSettings) {
    initialValues.partnerId = widgetSettings.partnerId;
  }

  if (trackerData) {
    initialValues.widgetId = trackerData.widgetId;
    initialValues.source = pick(trackerData, ['source', 'medium', 'campaign', 'content', 'term']);
  }

  if (
    (initialValues.contactGivenName &&
      initialValues.contactGivenName !== initialValues.patientGivenName) ||
    (initialValues.contactFamilyName &&
      initialValues.contactFamilyName !== initialValues.patientFamilyName)
  ) {
    initialValues.dependentStatus = DEPENDENT_STATUS.DEPENDENT as DependentStatus;
  }
  if (initialValues.source) {
    if (scheduleRequestAttemptData) {
      initialValues.source.channel = APPOINTMENT_CHANNELS.SCHEDULE_REQUEST;
    } else if (isScheduleRequest) {
      initialValues.source.channel = APPOINTMENT_CHANNELS.SCHEDULE_REQUEST;
    } else if (isOnWidget) {
      initialValues.source.channel = initialValues.partnerId
        ? APPOINTMENT_CHANNELS.PARTNERSHIP
        : APPOINTMENT_CHANNELS.WIDGET;
    } else {
      initialValues.source.channel = APPOINTMENT_CHANNELS.DIRECTORY;
    }
  }
  return initialValues;
}
